import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";

interface Props {
  page: number;            // 0-indexed
  perPage: number;
  total: number;
  perPageOptions?: number[];
  onPage: (next: number) => void;
  onPerPage: (next: number) => void;
}

/** Compact pagination footer used by the admin tables.
 *  Total is the *filtered* row count (X-Total-Count from the API). */
export function Pagination({
  page, perPage, total, onPage, onPerPage,
  perPageOptions = [25, 50, 100, 200],
}: Props) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(page, totalPages - 1);
  const start = total === 0 ? 0 : current * perPage + 1;
  const end = Math.min(total, (current + 1) * perPage);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 sm:px-4 py-2 text-xs text-slate-600 border-t border-slate-200 bg-slate-50">
      <span className="tabular-nums">
        {total === 0 ? "0" : `${start.toLocaleString()}–${end.toLocaleString()}`} of{" "}
        <strong>{total.toLocaleString()}</strong>
      </span>
      <div className="flex items-center gap-1 ml-auto">
        <label htmlFor="per-page" className="text-slate-500 hidden sm:inline">Per page</label>
        <select
          id="per-page"
          aria-label="Rows per page"
          className="input !w-auto !py-1 !px-2 !text-xs"
          value={perPage}
          onChange={(e) => onPerPage(Number(e.target.value))}
        >
          {perPageOptions.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-0.5">
        <button
          type="button" className="btn-ghost !px-1.5 !py-1 hidden sm:inline-flex"
          disabled={current <= 0}
          onClick={() => onPage(0)}
          aria-label="First page"
        ><ChevronsLeft className="h-4 w-4" /></button>
        <button
          type="button" className="btn-ghost !px-1.5 !py-1"
          disabled={current <= 0}
          onClick={() => onPage(current - 1)}
          aria-label="Previous page"
        ><ChevronLeft className="h-4 w-4" /></button>
        <span className="px-2 tabular-nums">
          {current + 1} / {totalPages}
        </span>
        <button
          type="button" className="btn-ghost !px-1.5 !py-1"
          disabled={current >= totalPages - 1}
          onClick={() => onPage(current + 1)}
          aria-label="Next page"
        ><ChevronRight className="h-4 w-4" /></button>
        <button
          type="button" className="btn-ghost !px-1.5 !py-1 hidden sm:inline-flex"
          disabled={current >= totalPages - 1}
          onClick={() => onPage(totalPages - 1)}
          aria-label="Last page"
        ><ChevronsRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
